import type * as C from "@zooi/contracts";
import type { StatusKind } from "./ui/status-kinds.ts";
import { executionStatus, type ExecutionAction } from "./action-items.ts";

// What the week page shows (docs/design-system.md §0.4c, plan M7 step 7e),
// kept apart from the components so the rules are tested. The server gives
// the week's bounds; plans, actions and campaigns come from their own reads.

const within = (iso: string | null, week: Pick<C.WeekView, "starts_at" | "ends_at">) =>
  iso !== null && iso >= week.starts_at && iso < week.ends_at;

/** An action's state as a status icon (design system §0.8). */
export function actionStatus(action: ExecutionAction & Pick<C.Action, "reading">): StatusKind {
  const execution = executionStatus(action);
  if (execution) return execution;
  switch (action.status) {
    case "queued": return "approved";
    case "committed": return "running";
    case "delivered": return "delivered";
    case "live": return "observing";
    case "reading_due": return "due";
    case "evaluated": return action.reading ? (action.reading.value === "no_signal" ? "none" : action.reading.value) : "done";
    case "failed": return "failed";
    case "expired": return "expired";
    default: return "stopped";
  }
}

/** A plan's state as a status icon (§0.3b): approval is a decision, not finished work, and a plan with no work is "nothing this week". */
export function planStatus(status: C.AgentPlan["status"]): StatusKind {
  switch (status) {
    case "proposed": return "proposed";
    case "approved": return "approved";
    case "recorded": return "none";
    case "rejected": return "rejected";
    case "expired": return "expired";
    default: return "stopped";
  }
}

/** The plans submitted in the week, newest first. */
export const plansOfWeek = (plans: readonly C.AgentPlan[], week: Pick<C.WeekView, "starts_at" | "ends_at">) =>
  plans.filter((plan) => within(plan.created_at, week)).sort((a, b) => b.created_at.localeCompare(a.created_at));

const OPEN: C.Action["status"][] = ["queued", "committed", "delivered"];
const ORDER: C.Action["status"][] = ["reading_due", "committed", "queued", "delivered", "live", "evaluated", "failed", "expired", "dismissed", "withdrawn"];

/**
 * The week's actions: approved in the week, and in the current week also the
 * ones approved earlier that are still being done. Observing and readings
 * have their own sections.
 */
export function actionsOfWeek(actions: readonly C.Action[], week: Pick<C.WeekView, "starts_at" | "ends_at" | "current">) {
  return actions
    .filter((action) => within(action.committed_at, week) || (week.current && OPEN.includes(action.status)))
    .sort((a, b) => ORDER.indexOf(a.status) - ORDER.indexOf(b.status) || (b.committed_at ?? "").localeCompare(a.committed_at ?? ""));
}

/** Live actions whose window has not matured: early signals only, never a result. */
export const observingActions = (actions: readonly C.Action[], now: number) =>
  actions.filter((action) => action.status === "live" && action.matures_at !== null && Date.parse(action.matures_at) > now);

/** Approved agent work that no agent has claimed yet: a run picks it up. */
export const awaitingClaim = (actions: readonly C.Action[]) =>
  actions.filter((action) => action.executor === "agent" && executionStatus(action) === "approved");

export interface ObservationWindow {
  key: string;
  /** An action's title is a reason the interface words; a campaign's is the agent's text. */
  title: C.Reason | string;
  /** "action" or "campaign", for where the row leads. */
  kind: "action" | "campaign";
  id: string;
  status: "observing" | "measuring";
  /** Which day of the window today is, from 1; null while the window has no start or length. */
  day: number | null;
  days: number | null;
}

/**
 * Open observation windows: actions from going live to maturing, measuring
 * campaigns from their window start. Days are counted as on the playbook
 * page (whole days since the start, from 1); only the count is shown, since
 * early numbers are not a result (vision §8.2).
 */
export function observationWindows(actions: readonly C.Action[], campaigns: readonly C.CampaignOverview[], now: number): ObservationWindow[] {
  const DAY = 86_400_000;
  const dayOf = (start: string | null, days: number | null) => (start && days ? Math.min(days, Math.max(1, Math.floor((now - Date.parse(start)) / DAY) + 1)) : null);
  return [
    ...observingActions(actions, now).map((action): ObservationWindow => {
      const days = action.activated_at && action.matures_at ? Math.max(1, Math.floor((Date.parse(action.matures_at) - Date.parse(action.activated_at)) / DAY)) : null;
      return { key: `action:${action.id}`, title: action.title, kind: "action", id: action.id, status: "observing", day: dayOf(action.activated_at, days), days };
    }),
    ...campaigns.filter((campaign) => campaign.phase === "measuring").map((campaign): ObservationWindow => (
      { key: `campaign:${campaign.id}`, title: campaign.title, kind: "campaign", id: campaign.id, status: "measuring", day: dayOf(campaign.window_start, campaign.window_days), days: campaign.window_days })),
  ];
}

/**
 * Getting started, in the order it happens (plan M7 step 7j, as on the add
 * product page): the agent connects, its first read proposes the facts the
 * person confirms, a schedule is set up to wake the agent (decided
 * 2026-11-03), then a first run and a first plan. An account with nothing for
 * zooi to read also names what to watch, which is the server's word.
 */
export const SETUP_STEPS = ["agent", "facts", "queries", "wakeup", "run", "plan"] as const;
export type SetupStep = (typeof SETUP_STEPS)[number];

export function setupSteps(setup: C.WeekView["setup"]): { step: SetupStep; done: boolean }[] {
  const done: Record<SetupStep, boolean | null> = { agent: setup.agent_connected, facts: setup.facts_confirmed, queries: setup.search_queries, wakeup: setup.wakeup_seen, run: setup.first_run, plan: setup.first_plan_approved };
  return SETUP_STEPS.flatMap((step) => {
    const state = done[step];
    return state === null ? [] : [{ step, done: state }];
  });
}

export type RunState =
  | { kind: "paused"; until: string }
  | { kind: "requested" | "running" | "reported" | "missed"; at: string }
  | { kind: "scheduled"; at: string }
  | { kind: "none" };

/**
 * Where the week's weekly run stands: asked for, under way, reported or
 * missed; else, in the current week, when it is next asked for.
 */
export function runState(week: Pick<C.WeekView, "run" | "current">, nextRunAt: string | null): RunState {
  const weekly = week.run.requests.filter((request) => request.kind === "weekly").at(-1);
  if (weekly) {
    if (weekly.status === "completed") return { kind: "reported", at: weekly.completed_at ?? weekly.due_at };
    if (weekly.status === "started") return { kind: "running", at: weekly.started_at ?? weekly.due_at };
    if (weekly.status === "missed" || weekly.status === "expired") return { kind: "missed", at: weekly.deadline_at };
    return { kind: "requested", at: weekly.due_at };
  }
  if (!week.current) return { kind: "none" };
  if (week.run.paused_until) return { kind: "paused", until: week.run.paused_until };
  return nextRunAt ? { kind: "scheduled", at: nextRunAt } : { kind: "none" };
}

/** The inbox items of one product, readings to confirm included, in the inbox's order. */
export const weekInbox = (items: readonly C.InboxItem[], productId: string) => items.filter((item) => item.product.id === productId);

export interface RunRow {
  id: string;
  kind: C.RunRequest["kind"];
  status: StatusKind;
  state: "scheduled" | "waiting" | "dispatched" | "running" | "reported" | "missed";
  at: string;
}

/**
 * Every run request of the week, weekly and off-cycle (§0.3b runs): booked
 * until the person is told, then waiting until its deadline; under way,
 * reported, or missed.
 */
export const runRows = (requests: readonly C.RunRequest[], now: number): RunRow[] => requests.map((request): RunRow => {
  const row = { id: request.id, kind: request.kind };
  switch (request.status) {
    case "completed": return { ...row, status: "delivered", state: "reported", at: request.completed_at ?? request.due_at };
    case "started": return { ...row, status: "running", state: "running", at: request.started_at ?? request.due_at };
    case "missed": case "expired": return { ...row, status: "expired", state: "missed", at: request.deadline_at };
    // zooi sent it to the product's dispatch target (§2.5, 2026-11-03): the runtime should start it; still the person's if it does not.
    default: return request.dispatched_at
      ? { ...row, status: "yours", state: "dispatched", at: request.dispatched_at }
      : Date.parse(request.notify_at) <= now
        ? { ...row, status: "yours", state: "waiting", at: request.deadline_at }
        : { ...row, status: "approved", state: "scheduled", at: request.due_at };
  }
});
